import { Box, Flex, Grid } from "@chakra-ui/layout";
import { ItemLevels } from "lib/hooks/usePackAssets";
import { FC, useEffect, useRef, useState } from "react";
import { DropTargetMonitor, useDrop, XYCoord } from "react-dnd";
import { DndDropZone } from "./dndDropZone";
import { SingleAsset } from "./singleAsset";

type Props = {
  data: any;
  handleDrop: Function;
  path: string;
  selectedAssets: any[];
  layout: any;
}

enum Side {
  LEFT = 'left',
  RIGHT = 'right',
}

export const DndGroupRow: FC<Props> = ({
  data, handleDrop, path, selectedAssets, layout
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ visibleDropZone, setVisibleDropZone] = useState<Side.LEFT | Side.RIGHT | undefined>(undefined);

  const hasMultipleChildren = data.children.length > 1;

  const [{ isOver }, drop] = useDrop({
    accept: [ ItemLevels.ASSET, ItemLevels.ROW ],
    canDrop(item: any) {
      if (selectedAssets.length > 1) {
        return false;
      }

      if (item.level === ItemLevels.GROUP) {
        const groupIndex = item.path;

        if (path.startsWith(groupIndex)) {
          return false;
        }
      }

      // Dragging an item within the same row
      // This is the exception to the can drop rule below
      if (item.path.startsWith(path)) {
        return data.children.length !== 1;
      }

      // If row has more than one asset already in it
      return data.children.length <= 1;
    },
    hover(item: any, monitor: DropTargetMonitor){
      if (!ref.current || !monitor.canDrop()) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const elemWidth = hoverBoundingRect.right - hoverBoundingRect.left;
      const hoverMiddleX = elemWidth / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientLeft = (clientOffset as XYCoord).x - (hoverBoundingRect.left / 100 * 50);
      const hoverClientRight = (clientOffset as XYCoord).x - (hoverBoundingRect.left / 100 * 150);

      if ((hoverClientLeft + (elemWidth / 100 * 5)) < hoverMiddleX) {
        setVisibleDropZone(Side.LEFT);
        return;
      }

      if (hoverClientRight - (elemWidth / 100 * 5) > hoverMiddleX) {
        setVisibleDropZone(Side.RIGHT);
        return
      }

      setVisibleDropZone(undefined);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    })
  }, [ ref, data, path ])

  useEffect(() => {
    if (!isOver) {
      setVisibleDropZone(undefined);
    }
  }, [ isOver ]);

  drop(ref)

  return (
    <Flex ref={ref} justifyContent="space-between">
      <Box
        layerStyle={[ 'with-long-transition' ]}
        width={visibleDropZone === Side.LEFT ? 'calc(50% - 10px)' : '0%'}
        marginRight={(visibleDropZone === Side.LEFT) ? "20px" : 0}
        flex="0 0 auto"
      >
        <DndDropZone
          layout={layout}
          data={{
            path: `${path}-0`,
            childrenCount: data.children.length,
            hasMultipleChildren,
          }}
          onDrop={handleDrop}
        />
      </Box>
      <Box
        width={visibleDropZone ? '50%' : '100%'}
        layerStyle={[ 'with-long-transition' ]}
        flex="1 1 auto"
      >
        <Grid templateColumns={`repeat(${data.children.length}, 1fr)`} gap="20px">
          {data.children.map((item, index) => (
            <SingleAsset
              key={item.id}
              data={item}
              path={`${path}-${index}`}
              selectedAssets={selectedAssets}
            />
          ))}
        </Grid>
      </Box>
      <Box
        width={visibleDropZone === Side.RIGHT ? 'calc(50% - 10px)' : '0%'}
        layerStyle={[ 'with-long-transition' ]}
        marginLeft={visibleDropZone === Side.RIGHT ? "20px" : 0}
        flex="0 0 auto"
      >
        <DndDropZone
          layout={layout}
          data={{
            path: `${path}-1`,
            childrenCount: data.children.length,
            hasMultipleChildren,
          }}
          onDrop={handleDrop}
        />
      </Box>
    </Flex>
  )
}
